import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_URL } from '../../utils/constant';

// API base URL - adjust this based on your API configuration
const API_BASE_URL = API_URL;

// Async thunks for CRUD operations

// CREATE: Create a new product attribute
export const createProductAttribute = createAsyncThunk(
  'productAttribute/create',
  async (attributeData, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/product-attributes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(attributeData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData.message || 'Failed to create product attribute');
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// READ: Get all product attributes for a company
export const fetchProductAttributes = createAsyncThunk(
  'productAttribute/fetchAll',
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/product-attributes/${companyId}`);

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData.message || 'Failed to fetch product attributes');
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// READ: Get a single product attribute by ID
export const fetchProductAttributeById = createAsyncThunk(
  'productAttribute/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/product-attributes/single/${id}`);

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData.message || 'Failed to fetch product attribute');
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// UPDATE: Update a product attribute
export const updateProductAttribute = createAsyncThunk(
  'productAttribute/update',
  async ({ id, updateData }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/product-attributes/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData.message || 'Failed to update product attribute');
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// DELETE: Delete a product attribute
export const deleteProductAttribute = createAsyncThunk(
  'productAttribute/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/product-attributes/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData.message || 'Failed to delete product attribute');
      }

      return { id, message: 'Product attribute deleted successfully' };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Initial state
const initialState = {
  attributes: [],
  currentAttribute: null,
  loading: false,
  error: null,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,
  fetchLoading: false,
};

// Product Attribute Slice
const productAttributeSlice = createSlice({
  name: 'productAttribute',
  initialState,
  reducers: {
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    // Clear current attribute
    clearCurrentAttribute: (state) => {
      state.currentAttribute = null;
    },
    // Reset state
    resetProductAttributeState: (state) => {
      state.attributes = [];
      state.currentAttribute = null;
      state.loading = false;
      state.error = null;
      state.createLoading = false;
      state.updateLoading = false;
      state.deleteLoading = false;
      state.fetchLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // CREATE
      .addCase(createProductAttribute.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createProductAttribute.fulfilled, (state, action) => {
        state.createLoading = false;
        state.attributes.push(action.payload);
        state.error = null;
      })
      .addCase(createProductAttribute.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
      })

      // FETCH ALL
      .addCase(fetchProductAttributes.pending, (state) => {
        state.fetchLoading = true;
        state.error = null;
      })
      .addCase(fetchProductAttributes.fulfilled, (state, action) => {
        state.fetchLoading = false;
        state.attributes = action.payload;
        state.error = null;
      })
      .addCase(fetchProductAttributes.rejected, (state, action) => {
        state.fetchLoading = false;
        state.error = action.payload;
      })

      // FETCH BY ID
      .addCase(fetchProductAttributeById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductAttributeById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAttribute = action.payload;
        state.error = null;
      })
      .addCase(fetchProductAttributeById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateProductAttribute.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateProductAttribute.fulfilled, (state, action) => {
        state.updateLoading = false;
        const index = state.attributes.findIndex(attr => attr.id === action.payload.id);
        if (index !== -1) {
          state.attributes[index] = action.payload;
        }
        if (state.currentAttribute && state.currentAttribute.id === action.payload.id) {
          state.currentAttribute = action.payload;
        }
        state.error = null;
      })
      .addCase(updateProductAttribute.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteProductAttribute.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteProductAttribute.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.attributes = state.attributes.filter(attr => attr.id !== action.payload.id);
        if (state.currentAttribute && state.currentAttribute.id === action.payload.id) {
          state.currentAttribute = null;
        }
        state.error = null;
      })
      .addCase(deleteProductAttribute.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      });
  },
});

// Export actions
export const { clearError, clearCurrentAttribute, resetProductAttributeState } = productAttributeSlice.actions;

// Export selectors
export const selectAllProductAttributes = (state) => state.productAttribute.attributes;
export const selectCurrentProductAttribute = (state) => state.productAttribute.currentAttribute;
export const selectProductAttributeLoading = (state) => state.productAttribute.loading;
export const selectProductAttributeError = (state) => state.productAttribute.error;
export const selectCreateLoading = (state) => state.productAttribute.createLoading;
export const selectUpdateLoading = (state) => state.productAttribute.updateLoading;
export const selectDeleteLoading = (state) => state.productAttribute.deleteLoading;
export const selectFetchLoading = (state) => state.productAttribute.fetchLoading;

// Export reducer
export default productAttributeSlice.reducer;
